import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, ExternalLink, Sparkles, Copy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { copyText } from "@/lib/format";
import { Section } from "@/components/xd/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const LANG_LABEL = { he: "עברית", ar: "العربية" };
const LANGS = ["he", "ar"];
const VERSION = { broker: "3.1", dealer: "1.1" };
const UPDATED = "2026-10-20";

const fetchGuide = (role, lng) =>
  api.get(`/${role}/assistant-guide`, { params: { lang: lng }, responseType: "text", transformResponse: (x) => x })
    .then(({ data, headers }) => ({ text: data.replace(/^\uFEFF/, ""), name: (headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] }));

const ManualCopy = ({ open, onClose, text, lng }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const selectAll = () => { ref.current?.focus(); ref.current?.setSelectionRange(0, text.length); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="assistant-manual-dialog">
        <DialogHeader>
          <DialogTitle>{t("assist.manual_title")} — {LANG_LABEL[lng]}</DialogTitle>
          <DialogDescription>{t("assist.copy_failed")}</DialogDescription>
        </DialogHeader>
        <textarea ref={ref} readOnly value={text} dir="auto" onFocus={selectAll} className="w-full h-72 bg-black/40 border border-white/15 p-3 text-[16px] sm:text-sm leading-relaxed" data-testid="assistant-manual-text" />
        <button type="button" className="btn-xd justify-center" onClick={selectAll} data-testid="assistant-manual-select-all">{t("assist.select_all")}</button>
      </DialogContent>
    </Dialog>
  );
};

const useGuides = (role) => {
  const [guides, setGuides] = useState({});
  const [failed, setFailed] = useState(false);
  const load = () => {
    setFailed(false);
    Promise.all(LANGS.map((l) => fetchGuide(role, l)))
      .then(([he, ar]) => setGuides({ he, ar }))
      .catch(() => setFailed(true));
  };
  useEffect(() => { load(); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps
  return { guides, failed, load };
};

const CopyButton = ({ role, lng, guide, onCopy }) => {
  const { t } = useLang();
  return (
    <button type="button" className="btn-xd w-full justify-center !min-h-[3.25rem] flex-col !gap-0.5" onClick={() => onCopy(lng)} disabled={!guide} data-testid={`assistant-copy-full-${role}-${lng}`}>
      <span className="flex items-center gap-2 font-medium">{guide ? <Copy size={16} /> : <Loader2 size={16} className="animate-spin" />}{t("assist.copy_full")} — {LANG_LABEL[lng]}</span>
      <span className="text-[11px] opacity-75 ltr-num" data-testid={`assistant-size-${role}-${lng}`}>{guide ? `v${VERSION[role]} · ${guide.text.length.toLocaleString()} ${t("assist.chars")}` : t("assist.loading")}</span>
    </button>
  );
};

const saveFile = ({ text, name }, role, lng) => {
  const url = URL.createObjectURL(new Blob(["\uFEFF" + text], { type: "text/markdown;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name || `XD-${role === "broker" ? "Broker" : "Dealership"}-Assistant-Guide-${lng.toUpperCase()}.md` });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

export const AssistantSection = ({ role }) => {
  const { t } = useLang();
  const { guides, failed, load } = useGuides(role);
  const [manual, setManual] = useState(null);
  const copy = async (lng) => {
    const ok = await copyText(guides[lng].text);
    if (ok) toast.success(t("assist.copied_full"));
    else { toast.error(t("assist.copy_failed")); setManual(lng); }
  };
  return (
    <Section title={t(`assist.title_${role}`)} testid="assistant-section">
      <p className="text-sm text-zinc-300 leading-relaxed flex items-start gap-2"><Sparkles size={16} className="mt-0.5 shrink-0 text-amber-300" />{t(`assist.text_${role}`)}</p>
      {failed && <button type="button" className="text-sm text-red-300 underline" onClick={load} data-testid="assistant-load-failed">{t("assist.load_failed")}</button>}
      <div className="grid sm:grid-cols-2 gap-3">
        {LANGS.map((l) => <CopyButton key={l} role={role} lng={l} guide={guides[l]} onCopy={copy} />)}
      </div>
      <ol className="text-xs text-zinc-400 space-y-1 list-decimal ps-5" data-testid="assistant-steps">{[1, 2, 3, 4].map((n) => <li key={n}>{t(`assist.s${n}`)}</li>)}</ol>
      <a className="btn-ghost w-full sm:w-auto justify-center" href="https://chatgpt.com/" target="_blank" rel="noreferrer" data-testid="assistant-open-chatgpt"><ExternalLink size={16} />{t("assist.open")}</a>
      <p className="text-[11px] text-zinc-500">{t("assist.no_auto_attach")}</p>
      <div className="border-t border-white/10 pt-3 space-y-2" data-testid="assistant-backup">
        <p className="text-[11px] text-zinc-500">{t("assist.backup")}</p>
        <div className="flex flex-wrap gap-2">
          {LANGS.map((l) => (
            <button key={l} type="button" className="btn-ghost !min-h-[2.5rem] text-xs" disabled={!guides[l]} onClick={() => saveFile(guides[l], role, l)} data-testid={`assistant-download-${role}-${l}`}>
              <Download size={14} />{LANG_LABEL[l]} <span className="opacity-60" dir="ltr">.md · {UPDATED}</span>
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-zinc-600">{t(`assist.disclaimer_${role}`)}</p>
      {manual && <ManualCopy open onClose={() => setManual(null)} text={guides[manual].text} lng={manual} />}
    </Section>
  );
};
